import { LinkButtonWithIcon } from "@/components/Buttons/LinkButtonWithIcon"
import FormattedEmail from "@/components/FormattedEmail"
import { UserSquareIcon } from "@/components/Icons"
import { EyeIcon } from "@/components/Icons"
import { SheetField, SheetFieldGrid, SheetSection } from "@/components/Sections/SheetSection"
import { Select } from "@/components/Select"
import { P3 } from "@/components/Typographies"
import { CollaboratorStatus } from "@/domain/collaborator/constants"
import { getCollaboratorLinkSheet } from "@/domain/collaborator/getCollaboratorLinkSheet"
import { getDisplayedName as getDisplayedCollaboratorName } from "@/domain/collaborator/getDisplayedName"
import { getDefaultUserCollaborator } from "@/domain/collaborator/getRelatedUserCollaborator"
import { NIL_UUID } from "@/domain/resources/constants"
import { SHARED_DATA_STATUS } from "@/domain/sharedData/constants"
import { SynchroSourcesMyBarnes } from "@/domain/synchro/constants"
import { useGenericErrorToast } from "@/hooks/useGenericErrorToast"
import useGetUserRoles from "@/hooks/useGetUserRoles"
import {
  type SharedDataJsonldReadCollectionSharedData,
  useGetCollaborators,
  useGetUser,
  useUpdateUserSharedData,
} from "@/networking/api"
import { isNotNullNorUndefined, isNullOrUndefined } from "@/utils/ts-utils"
import { Spinner, type SystemStyleObject } from "@chakra-ui/react"
import { useMemo } from "react"
import { useForm } from "react-hook-form"
import { FormattedMessage, useIntl } from "react-intl"
import { useNavigate } from "react-router-dom"

const styleSelectWithBorder = {
  container: (base: SystemStyleObject) => ({
    ...base,
    borderColor: "line",
  }),
  control: (base: SystemStyleObject) => ({
    ...base,
    borderColor: "line",
  }),
}

type Props = {
  sharedEntity: SharedDataJsonldReadCollectionSharedData
  refreshQueries?: () => void
}

export const ReceiverManagerSection = ({ sharedEntity, refreshQueries }: Props) => {
  const intl = useIntl()
  const navigateTo = useNavigate()

  const cluster = sharedEntity.toCluster
  if (isNullOrUndefined(cluster)) return <></>

  const { hasRoleBuAdmin } = useGetUserRoles()
  const isManager = hasRoleBuAdmin(cluster.id)

  const isEnded = [`${SHARED_DATA_STATUS.CLOSED}`, `${SHARED_DATA_STATUS.COMPLETED}`].includes(
    sharedEntity.status?.code ?? ""
  )

  const { data: userTo } = useGetUser(sharedEntity.to?.id ?? "", {
    query: { enabled: isNotNullNorUndefined(sharedEntity.to) },
  })
  const collaborator = useMemo(
    () => sharedEntity.toManager ?? getDefaultUserCollaborator(userTo?.collaborators),
    [sharedEntity, userTo]
  )

  const { control, register, getValues } = useForm<{ toManagerId: string }>({
    defaultValues: { toManagerId: collaborator?.id ?? NIL_UUID },
  })

  const { data: collaborators, isLoading } = useGetCollaborators({
    "cluster.cluster.id": cluster.id,
    "internalSource[]": SynchroSourcesMyBarnes,
    "status.code": CollaboratorStatus.ACTIVE,
    itemsPerPage: 300,
    withMinProps: true,
  })
  const options = useMemo(
    () =>
      (collaborators?.["hydra:member"] ?? []).map(c => ({
        value: c.id,
        label: getDisplayedCollaboratorName(c),
      })),
    [collaborators]
  )
  const optionSelected = useMemo(
    () => options.filter(c => c.value === collaborator?.id).shift(),
    [options, collaborator]
  )

  const { mutate: patchInterBu, isPending: isPendingPatch } = useUpdateUserSharedData()
  const genericErrorToast = useGenericErrorToast()
  const onChangeManager = () => {
    const form = getValues()

    patchInterBu(
      {
        data: {
          id: sharedEntity.id,
          ...form,
        },
      },
      {
        onSuccess: () => {
          refreshQueries?.()
        },
        onError: genericErrorToast,
      }
    )
  }

  return (
    <SheetSection
      title={intl.formatMessage({ id: "userSharedData.localManager" })}
      icon={UserSquareIcon}
    >
      <SheetFieldGrid>
        <SheetField label={intl.formatMessage({ id: "userSharedData.localManager.name" })}>
          {isNotNullNorUndefined(collaborator) ? (
            <>
              <P3 display="block">
                {getDisplayedCollaboratorName(collaborator)}
                <LinkButtonWithIcon
                  display="inline-block"
                  ml="2"
                  icon={EyeIcon}
                  onClick={() => navigateTo(getCollaboratorLinkSheet(collaborator))}
                />
              </P3>
              <P3 display="block">
                <FormattedEmail email={collaborator?.contactDetails?.[0]?.contactDetails?.email} />
              </P3>
            </>
          ) : (
            <P3 display="block" fontStyle="italic">
              (<FormattedMessage id="none" />)
            </P3>
          )}
        </SheetField>
        {isManager && !isEnded && (
          <SheetField label={intl.formatMessage({ id: "userSharedData.localManager.select_new" })}>
            {isLoading || isPendingPatch ? (
              <Spinner />
            ) : (
              <Select
                control={control}
                {...register("toManagerId")}
                options={options}
                value={optionSelected}
                isLoading={isLoading}
                onSelect={() => onChangeManager()}
                chakraStyles={styleSelectWithBorder}
              />
            )}
          </SheetField>
        )}
      </SheetFieldGrid>
    </SheetSection>
  )
}
